import React, { useCallback, useMemo, useState } from "react";
/**
 * VendorChatWindow - Vendor용 채팅 윈도우
 *
 * 공통 ChatWindow 컴포넌트를 사용하여 vendor 역할로 렌더링합니다.
 * slots를 통해 QuoteSheetModal, BaseQuoteModal을 커스터마이징합니다.
 */

import { useQueryClient, InfiniteData } from "@tanstack/react-query";
import {
  ChatWindow as SharedChatWindow,
  ChatWindowProps,
  ChatWindowSlots,
  InfoPanelSlotProps,
  PartyAvatar,
  QuoteCard,
  QuoteSheetModal,
  ContractSheetModal,
  type QuoteDetailSlotProps,
  type QuoteActionSlotProps,
  type ContractDetailSlotProps,
} from "@semo/shared-chat";
import { BaseQuoteModal } from "./modals/BaseQuoteModal";
import { calculateQuoteAmounts, type QuoteVatMode } from "../features/quotes";
import type {
  QuoteMessageResponseDto,
  CreateQuoteResponseVersionDto,
  QuoteItemDto,
  SupplierInfoDto,
  ChatControllerGetMessages200,
  CancellationRequestMessageResponseDto,
  AttachmentMetadataDto,
} from "@semo/api-contracts";
import {
  useChatControllerGetRoom,
  getChatControllerGetRoomQueryKey,
  useContractsControllerConfirmCancellation,
  useContractsControllerRejectCancellation,
  getChatControllerGetMessagesQueryKey,
  useStorageControllerUploadTemporaryFiles,
} from "@semo/api-contracts";
import { parseSpecification } from "@semo/shared-utils";
import { useVendorAuth as useDirectAuth } from "@/features/auth/application/hooks/useVendorAuth";

// ✅ Import UI Components from shared-ui
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Textarea,
  ButtonShadcn as Button,
} from "@semo/shared-ui";

import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import type { QuoteItem, QuoteFormFile } from "@/features/quotes";

interface VendorChatWindowProps {
  chatId: string;
  customerName: string;
  onClose: () => void;
  supplierId?: string; // Optional: For embed scenarios where auth is handled externally
  variant?: "modal" | "full";
  /**
   * 헤더 닫기(X) 숨김. c-market iframe 부모 모달처럼 컨테이너가 자체 닫기를 제공할 때 사용.
   * 기본 false — 기존 호출부 무영향.
   */
  hideClose?: boolean;
}

export function VendorChatWindow({
  chatId,
  customerName,
  onClose,
  variant = "modal",
  supplierId: propSupplierId,
  hideClose = false,
}: VendorChatWindowProps) {
  const queryClient = useQueryClient();

  // Using direct auth hook to get current user info
  const { currentUser } = useDirectAuth();

  // Use prop supplierId (embed) or current user id (dashboard)
  const supplierId = propSupplierId || currentUser?.id;

  const { data: joinData } = useChatControllerGetRoom(chatId);
  const { mutateAsync: uploadFiles } =
    useStorageControllerUploadTemporaryFiles();

  // ✅ Local state for dialogs
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    null,
  );
  const [rejectionReason, setRejectionReason] = useState("");

  // Backend determines if info should be blurred (Single Source of Truth)

  // For vendor, default to false (vendor can see their own info)
  // ChatRoomResponseDto doesn't have isAnonymous, defaulting to false for vendor
  const isBlur = false;

  /**
   * Handle contract completion
   * Invalidate cache to refresh anonymous status (blur -> clear)
   */
  const handleContractComplete = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getChatControllerGetRoomQueryKey(chatId),
    });
  }, [queryClient, chatId]);

  // ✅ Contract cancellation approval handler
  const { mutate: confirmCancel, isPending: isConfirming } =
    useContractsControllerConfirmCancellation({
      mutation: {
        onSuccess: () => {
          toast.success("계약 취소가 승인되었습니다. 환불 처리가 시작됩니다.");
          queryClient.invalidateQueries({
            queryKey: ["/chat/rooms", chatId, "messages"],
          });
          setConfirmApproveOpen(false);
          setSelectedContractId(null);
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message ||
              "취소 승인 중 오류가 발생했습니다.",
          );
          setConfirmApproveOpen(false); // Close on error too? Or keep open? Let's close for now.
        },
      },
    });

  // ✅ Contract cancellation reject handler
  const { mutate: rejectCancel, isPending: isRejecting } =
    useContractsControllerRejectCancellation({
      mutation: {
        onSuccess: () => {
          toast.success("계약 취소 요청이 반려되었습니다.");
          queryClient.invalidateQueries({
            queryKey: ["/chat/rooms", chatId, "messages"],
          });
          setRejectDialogOpen(false);
          setRejectionReason("");
          setSelectedContractId(null);
        },
        onError: (error: any) => {
          console.error("Reject failed:", error);
          toast.error(
            error?.response?.data?.message ||
              "반려 처리 중 오류가 발생했습니다.",
          );
        },
      },
    });

  const getContractIdFromMessage = useCallback(
    (messageId: string) => {
      const queryKey = [
        ...getChatControllerGetMessagesQueryKey(chatId),
        "infinite",
      ];
      const cachedData =
        queryClient.getQueryData<InfiniteData<ChatControllerGetMessages200>>(
          queryKey,
        );

      if (cachedData?.pages) {
        for (const page of cachedData.pages) {
          const foundMessage = page.data?.items?.find(
            (m) => m.id === messageId,
          );
          if (foundMessage?.messageType === "cancellation_request") {
            return foundMessage.cancellationRequestData?.contractId;
          }
        }
      }
      return joinData?.data?.contractId;
    },
    [chatId, joinData, queryClient],
  );

  const handleContractAction = useCallback(
    (messageId: string) => {
      const targetContractId = getContractIdFromMessage(messageId);

      if (targetContractId) {
        setSelectedContractId(targetContractId);
        setConfirmApproveOpen(true);
      } else {
        console.error("Contract Info Not Found. MessageId:", messageId);
        toast.error("계약 정보를 찾을 수 없습니다.");
      }
    },
    [getContractIdFromMessage],
  );

  const handleContractReject = useCallback(
    (messageId: string) => {
      const targetContractId = getContractIdFromMessage(messageId);

      if (!targetContractId) {
        toast.error("계약 정보를 찾을 수 없습니다.");
        return;
      }

      setSelectedContractId(targetContractId);
      setRejectionReason("");
      setRejectDialogOpen(true);
    },
    [getContractIdFromMessage],
  );

  const handleConfirmApprove = () => {
    if (selectedContractId) {
      confirmCancel({ id: selectedContractId });
    }
  };

  const handleConfirmReject = () => {
    if (!rejectionReason.trim()) {
      toast.error("반려 사유를 입력해주세요.");
      return;
    }
    if (selectedContractId) {
      rejectCancel({ id: selectedContractId, data: { rejectionReason } });
    }
  };

  // Memoize slots to prevent re-renders
  const slots = useMemo(
    () => ({
      // 견적 상세 모달 - 공용 컴포넌트 사용 (Clean Architecture)
      quoteDetailModal: (props: QuoteDetailSlotProps) => (
        <QuoteSheetModal
          message={props.message as QuoteMessageResponseDto}
          onClose={props.onClose}
          isBlur={isBlur}
          fileNameContext={props.fileNameContext}
        />
      ),
      // 계약 상세 모달
      contractDetailModal: (props: ContractDetailSlotProps) => (
        <ContractSheetModal
          contractId={props.contractId}
          onClose={props.onClose}
        />
      ),

      // 견적 수정 모달
      quoteActionModal: (props: QuoteActionSlotProps) => {
        const message = props.message as QuoteMessageResponseDto;

        // ✅ Adapter: Convert domain models (QuoteItem[], notes, files) → DTO
        const handleSubmit = async (
          items: QuoteItem[],
          notes: string,
          files: QuoteFormFile[],
          vatMode: QuoteVatMode,
        ): Promise<void> => {
          // 1. Handle file uploads if any
          let attachments: AttachmentMetadataDto[] = [];

          // Separate existing files and new files
          const existingFiles = files.filter((f) => f.isExisting && f.url);
          const newFiles = files
            .filter((f) => !f.isExisting && f.file)
            .map((f) => f.file) as File[];

          // Add existing files to attachments list
          attachments = existingFiles.map((f) => ({
            fileName: f.fileName,
            url: f.url!,
            filePath: f.filePath,
            fileSize: parseInt(f.fileSize.replace(/[^0-9]/g, "")) || 0, // Approximate
            contentType: "application/octet-stream", // Unknown for existing
          }));

          if (newFiles.length > 0) {
            try {
              const uploadResponse = await uploadFiles({
                data: { files: newFiles },
              });

              if (uploadResponse.success && uploadResponse.data?.attachments) {
                // ✅ Type-safe access to attachments array
                const uploadedFiles = uploadResponse.data.attachments;

                const newAttachments = uploadedFiles.map((file) => ({
                  fileName: file.fileName,
                  url: file.url,
                  filePath: file.filePath,
                  fileSize: file.fileSize,
                  contentType: file.mimeType,
                }));

                attachments = [...attachments, ...newAttachments];
              }
            } catch (error) {
              console.error("File upload failed:", error);
              toast.error("파일 업로드 중 오류가 발생했습니다.");
              return;
            }
          }

          // 2. Convert domain models to DTO
          const dtoItems: QuoteItemDto[] = items.map((item) => {
            // 규격은 최초 견적과 **같은 방식으로** 되돌린다. 서버가 규격 세 칸을 " / " 로
            // 합쳐 내려주므로, 한 칸에 뭉쳐 보내면 재견적 때만 칸 나눔이 풀린다.
            const specs = parseSpecification(item.specification);
            return {
              name: item.itemName,
              spec1: specs.spec1,
              spec2: specs.spec2,
              spec3: specs.spec3,
              quantity: item.quantity,
              // 공급사가 고른 단위를 그대로 보낸다. 종전엔 "EA" 로 못박아 보내서, 창에
              // «세트» 로 제대로 떠 있는 것을 그대로 보내도 «EA» 로 저장됐다.
              unit: item.unit || "개",
              unitPrice: item.unitPrice,
              amount: item.quantity * item.unitPrice,
            };
          });

          const { vatAmount: taxAmount, totalAmount } = calculateQuoteAmounts(
            items,
            vatMode,
          );

          const quoteData: CreateQuoteResponseVersionDto = {
            totalAmount,
            taxAmount,
            taxType: vatMode,
            items: dtoItems,
            description: notes || undefined,
            attachments: attachments.length > 0 ? attachments : undefined,
          };

          props.onSubmitRevision?.(quoteData);
        };

        return (
          <BaseQuoteModal
            mode="edit"
            variant={variant} // Pass variant down
            quoteData={{
              items: (message.quoteData?.items ?? []).map((item) => ({
                // ✅ Type-safe field mapping from OpenAPI QuoteMessageResponseDtoQuoteDataItemsItem
                // OpenAPI fields: name, spec, quantity, unit, unitPrice, amount
                // BaseQuoteModal expects: name, spec1, spec2, spec3, quantity, unit, unitPrice, amount
                name: item.name ?? "",
                spec1: item.spec ?? "",
                spec2: "", // Not in OpenAPI, default to empty
                spec3: "", // Not in OpenAPI, default to empty
                quantity: item.quantity ?? 0,
                unit: item.unit ?? "",
                unitPrice: item.unitPrice ?? 0, // OpenAPI.unitPrice → BaseQuoteModal.unitPrice
                amount: item.amount ?? 0,
                // Optional fields not in OpenAPI response
                itemCode: undefined,
                manufacturer: undefined,
                brand: undefined,
              })),
              description: message.quoteData?.description,
              // 이전 견적서의 단가 입력 축. 안 넘기면 창이 «별도» 로 초기화돼
              // 포함가로 쓴 견적의 총액이 10% 뛴다.
              taxType: message.quoteData?.taxType,
              buyerName: message.quoteData?.buyer?.name ?? customerName,
              categoryType: undefined,
              versionNumber: message.quoteData?.versionNumber,
              attachments: message.quoteData?.attachments, // ✅ Bind existing attachments
            }}
            supplierInfo={message.quoteData?.supplier as SupplierInfoDto | null}
            supplierId={supplierId}
            onClose={props.onClose}
            onSubmit={handleSubmit}
          />
        );
      },

      // 영수증 모달
      receiptModal: undefined,
    }),
    [isBlur, customerName, supplierId, variant, uploadFiles],
  ); // Add uploadFiles to deps

  if (!supplierId) {
    return null;
  }

  const content = (
    <SharedChatWindow
      roomId={chatId}
      chatRole="supplier"
      currentUserId={supplierId}
      supabaseClient={supabase}
      onClose={onClose}
      hideClose={hideClose}
      onContractComplete={handleContractComplete}
      onContractAction={handleContractAction}
      onContractReject={handleContractReject}
      slots={slots}
      variant={variant} // Pass variant down
    />
  );

  return (
    <>
      {variant === "modal" ? (
        <Dialog open={!!chatId} onOpenChange={(open) => !open && onClose()}>
          <DialogContent
            size="2xl"
            className="p-0 overflow-hidden h-[85vh] sm:max-w-6xl z-[200]"
          >
            {content}
          </DialogContent>
        </Dialog>
      ) : (
        content
      )}

      {/* ✅ Approval Confirmation Dialog */}
      <AlertDialog
        open={confirmApproveOpen}
        onOpenChange={setConfirmApproveOpen}
      >
        <AlertDialogContent className="z-[210]">
          <AlertDialogHeader>
            <AlertDialogTitle>계약 취소 승인</AlertDialogTitle>
            <AlertDialogDescription>
              정말로 계약 취소를 승인하시겠습니까?
              <br />
              승인 즉시 환불 처리가 진행되며, 이 작업은 되돌릴 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isConfirming}>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault(); // Prevent auto-close to handle async logic if needed, but here we handle it
                handleConfirmApprove();
              }}
              disabled={isConfirming}
              className="bg-red-600 hover:bg-red-700"
            >
              {isConfirming ? "처리 중..." : "승인하기"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ✅ Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="z-[210]">
          <DialogHeader>
            <DialogTitle>계약 취소 반려</DialogTitle>
            <DialogDescription>
              계약 취소 요청을 반려하는 사유를 입력해주세요.
              <br />
              구매자에게 반려 사유가 전달됩니다.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Textarea
              placeholder="예: 이미 배송이 시작되어 취소가 불가능합니다."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              disabled={isRejecting}
            >
              취소
            </Button>
            <Button
              onClick={handleConfirmReject}
              disabled={isRejecting || !rejectionReason.trim()}
            >
              {isRejecting ? "처리 중..." : "반려하기"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
